import { useEffect, useState } from "react";
import api, { formatApiError, downloadFile, previewFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Eye, Download, CalendarCheck } from "lucide-react";
import { toast } from "sonner";

const TONE = { pending: "pending", approved: "active", overdue: "overdue" };
const thisYear = new Date().getFullYear();

export default function CustomerVisits() {
  const { user } = useAuth();
  const firmId = user.firm_id;
  const [year, setYear] = useState(thisYear);
  const [months, setMonths] = useState(null);
  const [years, setYears] = useState([thisYear]);
  useEffect(() => {
    if (!firmId) return;
    api.get(`/firms/${firmId}/years`).then(({ data }) => {
      const ys = data.years && data.years.length ? data.years : [thisYear];
      setYears(ys);
      if (!ys.includes(year)) setYear(ys[0]);
    }).catch(() => {});
  }, [firmId]); // eslint-disable-line

  useEffect(() => {
    if (!firmId) { setMonths([]); return; }
    api.get(`/firms/${firmId}/visit-archive?year=${year}`).then(({ data }) => setMonths(data.months)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [firmId, year]);

  if (!firmId) return <p className="text-sm text-slate-500">Firma bağlantınız bulunamadı.</p>;

  return (
    <div className="space-y-5">
      <h1 className="font-display text-2xl font-bold text-slate-900">Ziyaret Raporları</h1>
      <div className="flex items-center gap-2">
        <Label className="text-sm">Yıl</Label>
        <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
          <SelectTrigger className="w-28" data-testid="cvisit-year-select"><SelectValue /></SelectTrigger>
          <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {months === null ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div> : (
        <div className="space-y-2">
          {months.map((m) => (
            <Card key={m.month} className="border-slate-200" data-testid={`cvisit-month-${m.month}`}>
              <CardContent className="py-3 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <CalendarCheck className={`h-5 w-5 shrink-0 ${m.pdf ? "text-sky-500" : "text-slate-300"}`} />
                  <div>
                    <div className="font-medium text-sm text-slate-800">{m.month_name} {m.year}</div>
                    <div className="text-xs text-slate-500">{m.pdf ? (m.actual_date ? `Ziyaret: ${formatDate(m.actual_date)}` : "") : "Rapor yayımlanmadı"}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {m.status && <StatusBadge tone={TONE[m.status]}>{m.status_label}</StatusBadge>}
                  {m.pdf && <>
                    <Button variant="ghost" size="sm" onClick={() => previewFile(`/documents/${m.pdf.id}/download`)} data-testid={`cvisit-preview-${m.month}`}><Eye className="h-4 w-4 mr-1" /> Önizle</Button>
                    <Button variant="ghost" size="sm" onClick={() => downloadFile(`/documents/${m.pdf.id}/download`, `${m.pdf.name}.pdf`)} data-testid={`cvisit-download-${m.month}`}><Download className="h-4 w-4 mr-1" /> İndir</Button>
                  </>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
